import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import RouterGroup from "../components/RouterGroup.vue";
import { checkCheckoutFrontendFreshness } from "@/services/frontend-freshness";
import { signinRedirectForRoute } from "@/utils/authRedirect";

Vue.use(VueRouter);

function reroute(original_to, new_path, next) {
  next({
    path: new_path,
    query: { next: original_to.fullPath || original_to.path },
  });
}

function hasActiveAccountHold() {
  const hold = store.getters["user/account_hold"];
  return Boolean(hold && hold.active);
}

function accountHoldRouteAllowed(to) {
  return (
    to.path === "/account-on-hold" ||
    to.path === "/signout" ||
    to.path === "/signin" ||
    to.path === "/forgot-password" ||
    to.path.startsWith("/reset-password") ||
    to.path === "/banking" ||
    to.path === "/investments/browse-all" ||
    to.path.startsWith("/pitch/")
  );
}

const routes = [
  {
    path: "/",
    component: () => import("@/views/Init.vue"),
  },
  {
    path: "/confirm",
    component: () => import("@/views/Confirm.vue"),
  },
  {
    path: "/invited",
    component: () => import("@/views/Invited.vue"),
  },
  {
    path: "/reset-password",
    component: () => import("@/views/Reset.vue"),
  },
  {
    // L7 (spec §8.3): unsubscribe landing page for home-follow
    // notification emails. Top-level / unauthenticated — the signed
    // token in the URL is the auth. Sits beside /reset-password
    // for the same reason: the URL needs to render even when the
    // user isn't logged in.
    path: "/home-follow/unsubscribe/:token",
    name: "HomeFollowUnsubscribe",
    component: () =>
      import(
        /* webpackChunkName: "homeFollowUnsubscribe" */ "@/views/HomeFollowUnsubscribe.vue"
      ),
    props: ({ params }) => ({ token: (params.token || "").trim() }),
  },
  {
    // Abandoned-cart spec §7.3: unsubscribe landing page for cart
    // reminder emails. Top-level / unauthenticated — the signed token
    // in the URL is the auth (same shape as home-follow above).
    // Future emails use a query token so static hosting can serve one
    // stable SPA shell object at /app/abandoned-cart/unsubscribe.
    path: "/abandoned-cart/unsubscribe",
    name: "AbandonedCartUnsubscribeQuery",
    component: () =>
      import(
        /* webpackChunkName: "abandonedCartUnsubscribe" */ "@/views/AbandonedCartUnsubscribe.vue"
      ),
    props: ({ query }) => ({
      token: (
        Array.isArray(query.token) ? query.token[0] : query.token || ""
      ).trim(),
    }),
  },
  {
    // Legacy route for previously generated token-path unsubscribe
    // links. The static host may not serve arbitrary token paths, but
    // keep the in-app route for any fallback/manual route object.
    path: "/abandoned-cart/unsubscribe/:token",
    name: "AbandonedCartUnsubscribe",
    component: () =>
      import(
        /* webpackChunkName: "abandonedCartUnsubscribe" */ "@/views/AbandonedCartUnsubscribe.vue"
      ),
    props: ({ params }) => ({ token: (params.token || "").trim() }),
  },
  {
    path: "/home",
    component: () => import("@/views/Home.vue"),
    beforeEnter(to, from, next) {
      if (store.state.init) {
        next();
        //if (store.getters['user/is_authenticated'] === true) {
        //    next()
        //} else {
        //    reroute(to, '/signin', next)
        //}
      } else {
        reroute(to, "/", next);
      }
    },
    children: [
      {
        path: "",
        redirect: "/investments/browse-all",
      },
      {
        path: "/forgot-password",
        component: () => import("@/views/ForgotPassword.vue"),
      },
      {
        path: "/signin",
        name: "signin",
        component: () => import("@/views/SignIn.vue"),
        props: ({ params }) => ({
          from: params.from,
        }),
      },
      {
        path: "/signup",
        component: () => import("@/views/SignUp.vue"),
      },
      {
        path: "/account-on-hold",
        meta: { requiresLogin: true },
        component: () => import("@/views/AccountOnHold.vue"),
      },
      {
        path: "/investments/:tab",
        component: () =>
          import(
            /* webpackChunkName: "propertiesBrowseAll" */ "@/views/Listings.vue"
          ),
        props: ({ params }) => ({ tab: params.tab === "browse-all" ? 1 : 2 }),
      },
      {
        path: "/community/:tab",
        component: () => import("@/views/LandingPageFeed.vue"),
        props: ({ params }) => ({ tab: params.tab === "feed" ? 1 : 2 }),
      },
      {
        path: "/notifications",
        name: "Notifications",
        meta: { noindex: true },
        component: () => import("@/views/Notifications.vue"),
      },
      {
        path: "/search",
        meta: { noindex: true },
        component: () => import("@/views/Search.vue"),
      },
      {
        path: "/investments/:state/:city/:id",
        component: () => import("@/views/HomeProfile.vue"),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      },
      {
        // Legacy abandoned-cart resume route used by older reminder
        // emails. Those links only contain the offering slug, not the
        // abandoned_cart_id needed to restore a specific checkout, so
        // fall back to the offering page instead of an empty checkout.
        path: "/pitch/:name/checkout-resume",
        meta: { noindex: true },
        beforeEnter(to, from, next) {
          next({
            path: `/pitch/${to.params.name}`,
            query: {
              ...to.query,
              checkout_resume_legacy: "1",
            },
          });
        },
      },
      {
        path: "/pitch/:name",
        component: () => import("@/views/HomeProfile.vue"),
        props: ({ params }) => ({ name: params.name }),
      },
      {
        path: "/myinvestment/:name",
        meta: { requiresLogin: true },
        component: () => import("@/views/MyInvestments.vue"),
        props: ({ params }) => ({ name: params.name }),
      },
      {
        path: "/my-investments/:id",
        meta: { requiresLogin: true },
        component: () => import("@/views/MyInvestments.vue"),
        props: ({ params }) => ({ id: Number(params.id) }),
      },
      {
        path: "/investors/:id",
        meta: { requiresLogin: true },
        name: "InvestorsPage",
        component: () => import("@/views/InvestorsPage.vue"),
        props: route => ({ id: Number(route.params.id) }) 
      },
      {
        path: "/investors/:id/referrals",
        meta: { requiresLogin: true },
        name: "ReferralCenter",
        component: () => import("@/views/ReferralCenter.vue"),
        props: route => ({ id: Number(route.params.id) }),
      },
      {
        path: "/users/:id",
        component: () => import("@/views/PublicProfile.vue"),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      },
      {
        path: "/investments/:state/:city/:id/money-allocation",
        component: () => import("@/views/HomeMoneyAllocation.vue"),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      },
      {
        path: "/strategies/:name",
        component: () => import("@/views/StrategyProfile.vue"),
        props: true,
      },
      {
        path: "/banking",
        meta: { requiresLogin: true },
        component: () => import("@/views/Banking.vue"),
      },
      {
        path: "/issuer/offerings/:home_id/closings",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerClosingList.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        path: "/issuer/offerings/:home_id/updates",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerOfferingUpdatesList.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        path: "/issuer/offerings/:home_id/updates/new",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerOfferingUpdate.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        // C6 (spec §8.1): issuer-facing abandoned-cart dashboard.
        // ``requiresLogin`` keeps the page behind auth; the C5
        // GET route enforces EditHomePermission server-side so
        // an issuer who happens onto another home's URL still
        // gets a 400+code=444 rather than a leak of the list.
        path: "/issuer/offerings/:home_id/abandoned-carts",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerAbandonedCarts.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        // Stable email entry point for issuer marketing-pixel alerts. The
        // static host can publish one exact /app/issuer/offerings/marketing
        // shell object with HTTP 200, then Vue redirects to the existing
        // offering-scoped dashboard without duplicating the screen.
        path: "/issuer/offerings/marketing",
        meta: { requiresLogin: true },
        beforeEnter(to, from, next) {
          const rawHomeId = Array.isArray(to.query.home_id)
            ? to.query.home_id[0]
            : to.query.home_id;
          const homeId = Number.parseInt(rawHomeId, 10);
          if (!Number.isFinite(homeId) || homeId <= 0) {
            next("/dashboard");
            return;
          }
          const query = { ...to.query };
          delete query.home_id;
          next({
            path: `/issuer/offerings/${homeId}/marketing`,
            query,
          });
        },
      },
      {
        // Pixels spec §7c: issuer marketing-tracking-pixels dashboard.
        // ``requiresLogin`` keeps the page behind auth; the issuer GET route
        // (app/homes/pixel_views.py) enforces EditHomePermission server-side,
        // so this icon is a discovery affordance, not the auth surface.
        path: "/issuer/offerings/:home_id/marketing",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerMarketingPixels.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        // Pixels spec §6a.1: issuer privacy-disclosure prerequisite. Lets the
        // issuer link their own privacy policy URL or opt into the
        // Invown-hosted notice before any pixel can activate. Ownership is
        // enforced server-side (app/homes/pixel_views.py).
        path: "/issuer/offerings/:home_id/marketing/privacy-disclosure",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/IssuerMarketingPrivacyDisclosure.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        // Pixels spec §6c.1/§7c.g: Locked Rule 204 ad-template generator.
        path: "/issuer/offerings/:home_id/marketing/template/new",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerMarketingTemplates.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        // Pixels spec §7c.g: issuer custom-creative submission for manual
        // Invown securities-marketing review.
        path: "/issuer/offerings/:home_id/marketing/creative/new",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerAdCreativeSubmit.vue"),
        props: route => ({ home_id: Number(route.params.home_id) || 0 }),
      },
      {
        path: "/issuer/closings/:closing_id",
        meta: { requiresLogin: true },
        component: () => import("@/views/IssuerClosingDetail.vue"),
        props: true,
      },
      {
        path: "/your-homes",
        component: () => import("@/views/YourHomes.vue"),
      },
      {
        path: "/your-pending-homes",
        component: () => import("@/views/YourPendingHomes.vue"),
      },
      {
        path: "/your-listed-homes",
        component: () => import("@/views/YourListedHomes.vue"),
      },
      {
        path: "/signout",
        component: () => import("@/views/SignOut.vue"),
      },
      {
        path: "/your-funded-homes",
        component: () => import("@/views/YourFundedHomes.vue"),
      },
      {
        path: "/your-account",
        component: () => import("@/views/YourAccount.vue"),
      },
      {
        path: "/dashboard",
        meta: { requiresLogin: true },
        component: () => import("@/views/Dashboard.vue"),
      },

      {
        path: "/list-your-offering/overview/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        // SR-3: rely on the global requiresLogin guard, which redirects a
        // signed-out deep link to /signin with `next` set so sign-in
        // returns the issuer to this offering. The former beforeEnter
        // never called next() and toggled a modal only mounted in Home.
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropSteps.vue"),
      },

      {
        path: "/list-your-offering/tell-us-about-yourself/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropTellUs.vue"),
      },
      {
        path: "/list-your-offering/property-address/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropAddress.vue"),
      },
      {
        path: "/list-your-offering/build-listing/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropBuildListing.vue"),
      },
      {
        path: "/list-your-offering/determine-equity/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropDetermineEquity.vue"),
      },
      {
        path: "/list-your-offering/propose-terms/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropProposeTerms.vue"),
      },
      {
        // Investor Incentives wizard step (perks + bonus shares). Spec:
        // docs/feature-specs/investor-incentives-wizard-step.md
        path: "/list-your-offering/investor-incentives/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import(
            "@/views/listYourProperty/ListYourPropInvestorIncentives.vue"
          ),
      },
      {
        path: "/list-your-offering/review/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropReview.vue"),
      },

      {
        path: "/list-your-offering/ready/:id",
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/listYourProperty/ListYourPropSubmitted.vue"),
      },

      // {
      //   path: "/list-your-offering/ready/:id",
      //   props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      //   component: () =>
      //     import("@/views/listYourProperty/ListYourPropReady.vue"),
      // },

      // {
      //   path: "/list-your-offering/review-and-publish/:id",
      //   props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      //   component: () =>
      //     import("@/views/listYourProperty/ListYourPropReviewAndPubulish.vue"),
      // },

      {
        path: "/application-portal",
        component: () => import("@/views/application/ApplicationPortal.vue"),
        children: [
          {
            path: "",
            component: () =>
              import("@/views/application/ApplicationPortalOverview.vue"),
          },
          {
            path: "personal-info",
            component: () =>
              import("@/views/application/ApplicationPortalPersonalInfo.vue"),
          },
          {
            path: "personal-info-2",
            component: () =>
              import("@/views/application/ApplicationPortalPersonalInfo2.vue"),
          },
          {
            path: "personal-info-3",
            component: () =>
              import("@/views/application/ApplicationPortalPersonalInfo3.vue"),
          },
          {
            path: "personal-info-4",
            component: () =>
              import("@/views/application/ApplicationPortalPersonalInfo4.vue"),
          },
          {
            path: "employment",
            component: () =>
              import("@/views/application/ApplicationPortalEmployment.vue"),
          },
          {
            path: "financial-profile",
            component: () =>
              import(
                "@/views/application/ApplicationPortalFinancialProfile.vue"
              ),
          },
          {
            path: "property-info",
            component: () =>
              import("@/views/application/ApplicationPortalPropertyInfo.vue"),
          },
          {
            path: "property-info-2",
            component: () =>
              import("@/views/application/ApplicationPortalPropertyInfo2.vue"),
          },
          {
            path: "property-info-3",
            component: () =>
              import("@/views/application/ApplicationPortalPropertyInfo3.vue"),
          },
          {
            path: "appraisal",
            component: () =>
              import("@/views/application/ApplicationPortalAppraisal.vue"),
          },
          {
            path: "money-allocation",
            component: () =>
              import(
                "@/views/application/ApplicationPortalMoneyAllocation.vue"
              ),
          },
          {
            path: "form-c",
            component: () =>
              import("@/views/application/ApplicationPortalFormC.vue"),
          },
          {
            path: "offering-details",
            component: () =>
              import(
                "@/views/application/ApplicationPortalOfferingDetails.vue"
              ),
          },
        ],
      },
      {
        path: "/add-bank-account",
        name: "Add bank account",
        meta: { requiresLogin: true },
        component: () => import("@/views/AddBankAccount.vue"),
      },
      {
        path: "/profile",
        name: "Profile",
        meta: { requiresLogin: true },
        component: () => import("@/views/Profile2.vue"),
      },
      {
        // Pixels Phase 4.1 (counsel §4.2): investor DSAR marketing-data export.
        path: "/settings/marketing-data",
        name: "MarketingDataExport",
        meta: { requiresLogin: true },
        component: () => import("@/views/MarketingDataExport.vue"),
      },
      {
        path: "/cart",
        component: () => import("@/views/Cart.vue"),
      },
      {
        path: "/checkout",
        meta: { requiresLogin: true },
        component: () => import("@/views/Checkout.vue"),
      },
      {
        path: "/reserve",
        component: () => import("@/views/Reservation.vue"),
      },
      {
        path: "/update-payment",
        component: () => import("@/views/PaymentUpdate.vue"),
      },
      {
        path: "/eversign",
        component: () => import("@/views/Eversign.vue"),
      },
      {
        path: "/portfolio",
        component: () => import("@/views/Portfolio.vue"),
        name: "Portfolio",
        meta: { requiresLogin: true },
      },
      {
        path: "/strategies",
        component: () => import("@/views/Strategies.vue"),
      },
      {
        path: "/strategies/update/:name",
        name: "UpdateStrategy",
        component: () => import("@/components/UpdateStrategy.vue"),
      },
      {
        path: "/create-strategy",
        name: "create-strategy",
        meta: { noindex: true },
        component: () => import("@/views/CreateStrategy.vue"),
        beforeEnter(to, from, next) {
          if (store.getters["user/is_authenticated"] === false) {
            next("/strategies");
          } else {
            next();
          }
        },
      },
      {
        path: "/favorites",
        component: () => import("@/views/Favorites.vue"),
        name: "Favorites",
        meta: { noindex: true },
      },
      {
        path: "/edit-profile",
        meta: { noindex: true },
        component: RouterGroup,
        children: [
          {
            path: "picture",
            component: () => import("@/views/EditProfilePicture.vue"),
          },
          {
            path: "dashboard",
            component: () =>
              import("@/components/dashboard/EditProfilePicture.vue"),
          },
        ],
      },
      {
        path: "/investor-calculators",
        name: "Calculators",
        component: () => import("@/views/InvestorCalculators.vue"),
      },
      {
        path: "/investments/:state/:city/:id/upload-pictures",
        component: () => import("@/views/UploadHomePicturesView.vue"),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      },
      //{
      //    path: '/messages',
      //    name: 'Messages',
      //    component: () => import('@/views/Messages.vue')
      //},
      //{
      //    path: '/messages/:id',
      //    name: 'UserMessages',
      //    props: true,
      //    component: () => import ('@/views/UserMessages.vue')
      //},
      {
        path: "/admin",
        meta: { noindex: true },
        component: () => import("@/views/admin/Admin.vue"),
        beforeEnter(to, from, next) {
          if (store.getters["user/user"].roles.indexOf("admin") === -1) {
            next("/home");
          } else {
            next();
          }
        },
        children: [
          {
            path: "",
            redirect: "overview",
          },
          {
            path: "overview",
            component: () => import("@/views/admin/AdminOverview.vue"),
          },
          {
            path: "users",
            component: () => import("@/views/admin/AdminUsers.vue"),
          },
          {
            path: "investments",
            component: () => import("@/views/admin/AdminInvestments.vue"),
          },
          {
            path: "hpi",
            component: () => import("@/views/admin/AdminHPI.vue"),
          },
          {
            path: "homes",
            component: () => import("@/views/admin/AdminHomes.vue"),
          },
          {
            path: "emails",
            component: () => import("@/views/admin/AdminEmails.vue"),
          },
          {
            path: "export",
            component: () => import("@/views/admin/AdminExport.vue"),
          },
          {
            path: "import",
            component: () => import("@/views/admin/AdminImport.vue"),
          },
          {
            path: "import-investment",
            component: () => import("@/views/admin/AdminNorthCapital.vue"),
          },
          {
            path: "errors",
            component: () => import("@/views/admin/AdminErrors.vue"),
          },
          {
            path: "strategies",
            component: () => import("@/views/admin/AdminStrategies.vue"),
          },
          {
            path: "file-upload",
            component: () => import("@/views/admin/AdminFileUpload.vue"),
          },
          {
            path: "ga",
            component: () => import("@/views/admin/AdminGA.vue"),
          },
          {
            path: "memos",
            component: () => import("@/views/admin/AdminMemos.vue"),
          },
          {
            path: "companies",
            component: () => import("@/views/admin/AdminCompanies.vue"),
          },
          {
            path: "update-engagebay-data",
            component: () => import("@/views/admin/AdminUpdateEngagebay.vue"),
          },
          {
            path: "enterprise-config",
            component: () => import("@/views/admin/AdminEnterpriseConfig.vue"),
          },
          {
            path: "banking-approvals",
            component: () => import("@/views/admin/AdminBankingApprovals.vue"),
          },
          {
            path: "banking",
            component: () => import("@/views/admin/AdminBanking.vue"),
          },
          {
            path: "browse-visibility",
            component: () => import("@/views/admin/AdminBrowseVisibility.vue"),
          },
          {
            // C7 (spec §8.2): admin view of every issuer's
            // abandoned carts. Falls under /admin so the admin
            // role-guard on the parent route applies; the C5 GET
            // route is the durable AdminPermission check.
            path: "abandoned-carts",
            component: () =>
              import("@/views/admin/AdminAbandonedCarts.vue"),
          },
          {
            path: "perks",
            component: () => import("@/views/admin/AdminPerks.vue"),
          },
          {
            // Pixels Phase 3 (§7d.c): admin custom ad-creative review queue.
            // Inherits the /admin parent's admin-role beforeEnter guard; the
            // backend routes are twice-gated (login + AdminPermission).
            path: "ad-creative-review",
            component: () =>
              import("@/views/admin/AdminAdCreativeReview.vue"),
          },
          {
            // Pixels §7d.d: provider DPA / SCC activation state. Inherits the
            // /admin parent guard; backend routes remain twice-gated.
            path: "marketing-pixels",
            component: () =>
              import("@/views/admin/AdminMarketingPixels.vue"),
          },
          {
            // Reg CF + 506(c) program, Phase D (story E6): per-Investment
            // bonus-share snapshot audit. Inherits the /admin parent's
            // admin-role beforeEnter guard.
            path: "bonus-share-audit",
            component: () =>
              import("@/views/admin/AdminBonusShareAudit.vue"),
          },
          {
            path: "enterprise-bank-config",
            redirect: "enterprise-config",
          },
          {
            path: "reconciliation",
            redirect: "reconciliation/dashboard",
          },
          {
            path: "reconciliation/upload",
            component: () =>
              import("@/views/admin/reconciliation/AdminBankStatementUpload.vue"),
          },
          {
            path: "reconciliation/dashboard",
            component: () =>
              import("@/views/admin/reconciliation/AdminBankStatementDashboard.vue"),
          },
          {
            path: "closings",
            component: () =>
              import("@/views/admin/closings/AdminClosingOfferingIndex.vue"),
          },
          // D2 Epic B — closings. Routes keyed off the home_id in the
          // URL so per-offering links stay stable and deep-linkable.
          {
            path: "offerings/:home_id/closings/new",
            component: () =>
              import("@/views/admin/closings/AdminClosingCreate.vue"),
            props: true,
          },
          {
            path: "offerings/:home_id/closings",
            component: () =>
              import("@/views/admin/closings/AdminClosingList.vue"),
            props: true,
          },
          {
            path: "closings/:closing_id",
            component: () =>
              import("@/views/admin/closings/AdminClosingDetail.vue"),
            props: true,
          },
          {
            path: "refunds",
            component: () => import("@/views/admin/refunds/AdminRefundsTracker.vue"),
          },
          {
            // Reg CF + 506(c) slice 4: accreditation review queue.
            path: "accreditation-queue",
            component: () =>
              import("@/views/admin/AccreditationQueue.vue"),
          },
          {
            // Reg CF + 506(c) Phase B: per-issuer verification queue.
            path: "accreditation-verifications",
            component: () =>
              import("@/views/admin/AccreditationVerificationQueue.vue"),
          },
          {
            path: "accreditation-verifications/:id",
            component: () =>
              import("@/views/admin/AccreditationDetail.vue"),
          },
          {
            path: "accreditation-audit-sample",
            component: () =>
              import("@/views/admin/AccreditationAuditSample.vue"),
          },
          {
            path: "aml",
            component: () =>
              import("@/views/admin/AdminAML.vue"),
          },
          {
            // Reg CF + 506(c) Phase C: payment_delivery_outbox inspection.
            path: "payment-delivery-outbox",
            component: () =>
              import("@/views/admin/AdminPaymentDeliveryOutbox.vue"),
          },
          {
            path: "enterprise-ach",
            component: () => import("@/views/admin/enterprise-ach/AdminEnterpriseACH.vue"),
            children: [
              {
                path: "",
                redirect: "board",
              },
              {
                path: "board",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseFundingBoard.vue"),
              },
              {
                path: "action-queue",
                redirect: (to) => ({
                  path: "/admin/enterprise-ach/board",
                  query: { ...to.query },
                }),
              },
              {
                path: "submissions",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHSubmissions.vue"),
              },
              {
                path: "refund-tracker",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHRefundTracker.vue"),
              },
              {
                path: "instruction-lookup",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHInstructionLookup.vue"),
              },
              {
                path: "failed-runs",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHFailedRuns.vue"),
              },
              {
                path: "submissions/:submissionId/portal-workspace",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHPortalWorkspace.vue"),
              },
              {
                path: "submissions/:submissionId",
                component: () =>
                  import("@/views/admin/enterprise-ach/AdminEnterpriseACHSubmissionDetail.vue"),
              },
            ],
          },
        ],
      },
      {
        path: "/analytics/:id",
        component: () => import("@/views/tools/Analytics.vue"),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
      },
      /* {
        path: '/tools',
        redirect: '/tools/offering-memo',
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/offering-memo',
        component: () => import('@/views/tools/Welcome.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/dashboard',
        redirect: '/tools/dashboard/my-memorandums',
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/dashboard/:tab',
        component: () => import('@/views/tools/Dashboard.vue'),
        props: ({ params }) => ({ currentPage: params.tab }),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/MemorandumOverview.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/property-address/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/PropertyAddress.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/tell-us/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        name: 'about-you',
        component: () => import('@/views/tools/TellUsAboutYourself.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/build-offering/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/BuildOffering.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/choose-plan/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/ChoosePlan.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/public-profile/:id',
        component: () => import('@/views/tools/PublicProfile.vue'),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/public-company-profile/:id',
        component: () => import('@/views/tools/CompanyPublicProfile.vue'),
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/memorandum-overview/summary/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/Summary.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      },
      {
        path: '/tools/listing/:id',
        props: ({ params }) => ({ id: Number.parseInt(params.id, 10) || 0 }),
        component: () => import('@/views/tools/Listing.vue'),
        beforeEnter(to, from, next) {
          if (store.getters['user/user'].roles.indexOf('admin') === -1) {
            next('/home');
          } else {
            next();
          }
        },
      }, */
      {
        path: "/invite",
        component: () => import("@/views/InviteSignup.vue"),
        meta: { guestOnly: true },
      },
      {
        path: "/post-close/setup",
        meta: { requiresLogin: true },
        component: () => import("@/views/post-close/setup/PostCloseSetup.vue"),
      },
      {
        path: "/post-close/offerings",
        meta: { requiresLogin: true },
        component: () => import("@/views/post-close/setup/PostCloseOfferings.vue"),
      },
      {
        path: "/onboarding/hub/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/OnboardingHub.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/onboarding/manual/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/ManualOnboarding.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/onboarding/ai/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/AIAssistedOnboarding.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/onboarding/checklist/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/OnboardingChecklist.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/onboarding",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/OnboardingWizard.vue"),
      },
      {
        path: "/onboarding/:id",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/onboarding/OnboardingWizard.vue"),
        props: route => ({ id: Number(route.params.id) }),
      },
      // A-1: Team Management
      {
        path: "/team/:accountId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/team/TeamManagement.vue"),
        props: route => ({ accountId: Number(route.params.accountId) }),
      },
      // A-2: Approval Workflows
      {
        path: "/approval/settings/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/approval/ApprovalSettings.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/approval/queue",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/approval/ApprovalQueue.vue"),
      },
      {
        path: "/approval/queue/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/approval/ApprovalQueue.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      // B-1: Audit Log (admin-only: global audit log + AML/compliance PII)
      {
        path: "/audit",
        meta: { requiresLogin: true },
        beforeEnter(to, from, next) {
          if (store.getters["user/user"].roles.indexOf("admin") === -1) {
            next("/home");
          } else {
            next();
          }
        },
        component: () =>
          import("@/views/post-close/audit/AuditLogViewer.vue"),
      },
      // Offering-scoped audit log: the backend enforces owner-or-admin per
      // offering, so this route stays login-only (an issuer reads the audit
      // trail for an offering they own).
      {
        path: "/audit/:offeringId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/audit/AuditLogViewer.vue"),
        props: route => ({ offeringId: Number(route.params.offeringId) }),
      },
      // B-2: Compliance Export (admin-only)
      {
        path: "/audit/compliance-export",
        meta: { requiresLogin: true },
        beforeEnter(to, from, next) {
          if (store.getters["user/user"].roles.indexOf("admin") === -1) {
            next("/home");
          } else {
            next();
          }
        },
        component: () =>
          import("@/views/post-close/audit/ComplianceExportWizard.vue"),
      },
      // D-1: Cap Table
      {
        path: "/cap-table/:homeId",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/cap-table/CapTableView.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/cap-table/:homeId/events",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/cap-table/CapTableEventLog.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/distribution/:homeId/ledger",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/distributions/DistributionLedger.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/distribution/:homeId/reconciliation",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/distributions/ReconciliationDashboard.vue"),
        props: route => ({ homeId: Number(route.params.homeId) }),
      },
      {
        path: "/payment-preferences",
        meta: { requiresLogin: true },
        component: () =>
          import("@/views/post-close/distributions/PaymentPreferences.vue"),
      },
      {
        path: "/*",
        component: () => import("@/views/NotFound.vue"),
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.path === "/checkout") {
    const checkoutFrontendFresh = await checkCheckoutFrontendFreshness({
      onReload: () => window.location.reload(),
    });
    if (!checkoutFrontendFresh) {
      return;
    }
  }

  const requiresLogin = to.matched.some((record) => record.meta.requiresLogin);
  if (
    store.getters["user/is_authenticated"] === null &&
    (requiresLogin || !accountHoldRouteAllowed(to))
  ) {
    await store.dispatch("user/get_user");
  }

  if (requiresLogin) {
    if (store.getters["user/is_authenticated"] === false) {
      return next(signinRedirectForRoute(to));
      // store.commit('toggleLoginModal');
    }
  }

  if (hasActiveAccountHold() && !accountHoldRouteAllowed(to)) {
    return next({ path: "/account-on-hold", query: { next: to.fullPath } });
  }

  return next();
});

// Function to handle expected navigation failures produced by redirects/guards.
const handleNavigationError = (error) => {
  const { isNavigationFailure, NavigationFailureType } = VueRouter;
  const expectedNavigationFailure = [
    NavigationFailureType.duplicated,
    NavigationFailureType.redirected,
    NavigationFailureType.cancelled,
    NavigationFailureType.aborted,
  ].some(type => isNavigationFailure(error, type));

  if (expectedNavigationFailure) {
    return;
  }

  throw error;
}

// Override the push method of the router to catch errors
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(handleNavigationError)
}

// Override the replace method of the router to catch errors
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(handleNavigationError)
}

export default router;
